/**
 * `invite` (§7.7, D38) — add members to a gate. Ported from `prototype/screens/invite.js` and
 * later merged with P15's bulk multi-select step (D97): **one screen, one growing list**, whether
 * the manager adds one person or forty. There is no longer a single-vs-bulk fork — a contact
 * picked once and a contact picked as part of a multi-select sweep become the same kind of row,
 * each with its own resident/manager choice (D38's role field, previously only offered on the
 * single path; §6's original "assign as residents" described the bulk step when it was its own
 * screen with no per-row field left to decide — logged as a deviation in
 * `implementation-notes.md` → P15).
 *
 * **The picker opens itself.** On mount, if the platform offers one (`navigator.contacts` on a
 * supporting browser, or `native/`'s D167 plugin), the multi-select Contact Picker is invoked
 * immediately — no tap required — so opening this screen is itself the "add people" gesture. On a
 * browser that requires the picker's `select()` call to sit inside a click's own user activation,
 * the mount-triggered call has none and the browser silently refuses it (never a `SecurityError`
 * this component throws for); the same "Add from contacts" button that would have opened it stays
 * on screen so the manager can retry from an actual tap. Native has no such rule and the picker
 * opens every time.
 *
 * **D242 gives that paragraph an exception, and the exception is the whole of the Android story.**
 * Android ships no permission-free multi-select contact intent, so `contactPickerMode` answers
 * `'address-book-read'` there and the multi path becomes a `READ_CONTACTS` read of the book into
 * a list of our own (`ContactBookSheet`). In that mode **the mount-time auto-open did not fire at
 * all**: a permission prompt raised from a mount effect has no user action behind it, which is
 * exactly what store review flags as out-of-context, so the grant is requested from the "Add from
 * contacts" tap and the rationale sits beside that button where a review screenshot catches it.
 * In `'os-picker'` mode — every browser — the mount-time auto-open is unchanged.
 *
 * **D340 put the iPhone in the book mode too, for search.** Apple's picker has no search field and
 * no API to add one, so iOS now reads the book behind the Contacts dialog into the same
 * `ContactBookSheet`, and every rule below — the tap before the first dialog, D250's auto-open once
 * granted, the silent fallback on a refusal — applies to it unchanged. Two things differ, and both
 * are the platform's rather than this screen's: **iOS 18's limited grant** is a grant (the list says
 * it shows only the contacts the manager shared, and carries a way to share more — even over an
 * empty list — which since D343 is this app's page in Settings, Apple's in-app button being blank
 * or dead on iOS 26+ hardware), and **the refusal's fallback is plural on iOS**,
 * Apple's picker being multi-select for free where Android's intent picks one row.
 *
 * **D250 narrows that suppression to the state that has a dialog in it, which is the state D242
 * was arguing about.** The Android container was the one surface where opening this screen did not
 * open a picker, and the difference was never a capability: Chrome's own picker is permission-free
 * to the *page* because Chrome holds `READ_CONTACTS` itself, so the PWA inherits a grant the
 * container has to own. Once the container owns it, there is nothing left to prompt for —
 * `readContactBook` reads `getPermissionState`, finds `GRANTED` and presents no dialog — so the
 * auto-open fires on every launch after the manager has granted once, and only the **first** visit
 * still costs the tap D242 requires. `contactBookAccess()` is the peek that decides, it prompts
 * nothing, and **every answer other than a positive grant keeps D242's tap exactly as it was**.
 *
 * **A refused grant is answered with silence at the tap, and since D344 with an offer beside the
 * button on the iPhone.** `readContactBook` reads `getPermissionState` before it prompts, so a
 * permanent denial is *named* rather than rediscovered on every open, and this screen falls
 * straight through to the permission-free OS picker — Android's at one contact a tap, iOS's still
 * multi-select (`osPickerMultiSelects`, D340). There is still no error surface: a declined grant is
 * a choice, not a failure. **What D344 adds is an offer, not a complaint** — an iPhone manager whose
 * Contacts access is *None* sees, under the button, that allowing access in Settings gives them
 * search, with a link there. The tap still opens Apple's picker exactly as before, so the line gates
 * nothing; it is drawn only for `'refused'` (never Android, never a Screen Time block), and when the
 * manager comes back with access on, the book opens.
 *
 * **The chosen numbers are all that leave.** A picked contact's name is display-only, kept
 * because a manager cannot check twenty bare `+9725…` rows against who they meant to invite —
 * a drop that is cosmetic at one row is disabling at twenty. It is deliberately **not** carried
 * into `memberLabel`: that is a declared User Content type, and auto-filling it from the address
 * book would silently convert third-party content into collected, transmitted data and falsify a
 * store form. The bulk payload is byte-for-byte what it was before D242.
 *
 * **No row carries a transport restriction any more, and every invite is the default: both
 * transports.** P16 gave each row a cycling transport chip; on a phone-width row it and the role
 * chip left the name almost no width and drew over it (device pass). The owner dropped it rather
 * than shrinking it: restricting a grant to one transport is rare and deliberate, and D269's member
 * sheet in People still sets it per person after the invite. The bulk endpoint still accepts
 * `transportRestriction` per row — nothing server-side moved; this screen simply never sends it.
 */
import { useEffect, useRef, useState } from 'react'
import type { E164, GateId, MemberRole } from '@selogate/core'
import { normalizePhone } from '@selogate/core'
import { useDeviceCache } from '../../device-cache/context'
import { useSession } from '../../auth/context'
import { NetworkError } from '../../auth/transport'
import {
  AppBar,
  ConfirmPopover,
  FrameOverlay,
  Icon,
  LinkButton,
  OutcomeReport,
  SecondaryButton,
  ShareSheet,
} from '../../components'
import type { OutcomeRowView, OutcomeTone } from '../../components'
import {
  contactBookAccess,
  contactPhoneSelections,
  contactPickerMode,
  contactPickerProperties,
  openContactSettings,
  osPickerMultiSelects,
  pickContacts,
  readContactBook,
  type ContactProperty,
  type PhoneKind,
  type PickedContact,
} from '../../device-capabilities/contacts'
import type { Key } from '../../i18n/catalogs'
import { useTranslation } from '../../i18n/I18nProvider'
import { useDismissOnBack, useNavigation } from '../../router'
import { getRoster, type RosterRow } from '../shared/api'
import { bulkInviteMembers, type BulkInviteResult, type InviteSource } from './bulkApi'
import { ContactAvatar } from './ContactAvatar'
import { ContactBookSheet, type ContactBookSelection } from './ContactBookSheet'
import { PHONE_KIND_KEYS } from './NumberChooser'

/**
 * **D346 (g) — "already on this gate" is the server's `skipped-duplicate` condition, restated.**
 * `packages/functions/members/invite.ts`'s `registerGrant` answers `skipped-duplicate` from two
 * writes in `members/grants.ts`: `putMembership` refuses any existing membership row (an account
 * holding the gate is `active`), and `putPendingGrant` refuses a live `pending` row but
 * **overwrites an `expired` one** — §7.7's re-send. So `active` and `pending` hold a live grant and
 * `expired` does not: greying an expired member would block the very re-invite the roster offers
 * a button for, and missing `pending` would grey too little. **Derived from the server, never from
 * People's roster chips**, which answer a different question (what to draw), and a row with no
 * `phone` cannot be matched against the book at all.
 */
export function holdsLiveGrant(row: RosterRow): boolean {
  return row.phone !== undefined && (row.status === 'active' || row.status === 'pending')
}

/**
 * `packages/functions/members/config.ts`'s `MAX_BULK_ROWS`, restated rather than imported —
 * `packages/functions/*` is server-only, which is the same reason `bulkApi.ts` declares
 * `InviteSource` locally. It bounds the address-book list's selection, never the wire: the
 * endpoint refuses a 101st row itself and does not truncate.
 */
const MAX_BULK_ROWS = 100

interface Entry {
  readonly phone: E164
  readonly source: InviteSource
  readonly role: MemberRole
  /**
   * D242 — display only, and never on the wire. Present when the contact surface handed one back;
   * `undefined` for a manually typed number, and for a contact filed under a number alone.
   */
  readonly name?: string
  /**
   * D346 (f) — the picked number's type from the book, display only (the collect row shows it
   * beside the number). Like `name`, it is not in the wire row's field list and never leaves.
   */
  readonly kind?: PhoneKind
}

/**
 * **`book` is the fourth phase, and its being a phase rather than an overlay is the ruling.** The
 * union is rendered by early return, so the list is a screen: no backdrop, no layer, and every
 * other phase is unaffected by what back does to this one.
 *
 * **Back on that phase does push one entry, and the reason it may is narrow.** This paragraph used
 * to end *"`useDismissOnBack` is not reached for"*, on a sound argument: this screen auto-opens its
 * picker on mount with no tap behind it, and Chrome marks a gesture-less `pushState` **skippable**,
 * so back steps straight past such an entry — D145's own stated failure. D242 answered it by
 * suppressing the auto-open in the one mode that reaches this phase, which made the tap on *Add
 * from contacts* the only door in. **D250 opens a second door that carries no gesture** — an
 * already-granted book opens itself at mount — so the answer moved from the phase to the push:
 * `auto` rides on this member, and where it is set the entry waits for the manager's first tick,
 * which is a gesture. Every other mode opens the OS picker and never enters `book` at all, so
 * nothing is pushed there. **The entry belongs to the phase, never to the confirm dialog it
 * raises** — a `pushState` issued from inside a `popstate` handler is the skippable one — which is
 * why a second back press while that dialog stands leaves the screen.
 *
 * **The book rides in this state and nowhere else.** It arrives once, lives exactly as long as
 * this phase, and is gone the moment the manager taps Add or Cancel — never a module-level
 * variable, which is the reflexive "don't re-read it every time" optimization and is forbidden by
 * name (`infra/tests/web-build-hygiene.test.ts`).
 */
type Phase =
  | { readonly kind: 'collect' }
  | {
      readonly kind: 'book'
      readonly contacts: readonly PickedContact[]
      /** D250 — opened by the mount effect rather than by a tap, so the back entry waits for one. */
      readonly auto: boolean
      /** D340 — iOS 18's limited grant: the list shows only the contacts the manager shared. */
      readonly limited: boolean
    }
  | { readonly kind: 'registering' }
  | { readonly kind: 'results'; readonly result: BulkInviteResult }

export function InviteScreen({ gateId }: { readonly gateId: GateId }): JSX.Element {
  const session = useSession()
  const deviceCache = useDeviceCache()
  const navigate = useNavigation()
  const { t, locale } = useTranslation('invite')

  const cached = deviceCache.gates.find((g) => g.gateId === gateId)
  const gateName = cached?.alias ?? cached?.defaultName ?? ''

  const [entries, setEntries] = useState<readonly Entry[]>([])
  const [phase, setPhase] = useState<Phase>({ kind: 'collect' })
  const [properties, setProperties] = useState<readonly ContactProperty[] | undefined>(undefined)
  const [pickerBusy, setPickerBusy] = useState(false)
  /**
   * Which of the two ways in is on screen. **`'contacts'` is the default and the pill does not
   * open it** — the segment being pre-selected is not a tap, and D242's whole posture is that
   * Android's `READ_CONTACTS` prompt comes from one. The segment reveals the button; the button
   * still raises the prompt.
   */
  const [tab, setTab] = useState<'contacts' | 'manual'>('contacts')
  /**
   * What the book currently has ticked, mirrored up so the app bar's chevron and the phone's back
   * gesture can ask about it. Cleared on the way out of the phase rather than on the way in: a
   * stale list surviving into the next open would offer to add contacts nobody ticked this time.
   */
  const [bookSelection, setBookSelection] = useState<readonly ContactBookSelection[]>([])
  /** The leave-confirm the two back affordances raise. Not a phase — the book stays mounted under it. */
  const [leaveConfirm, setLeaveConfirm] = useState(false)
  /**
   * D250 — whether the book's D145 entry has been pushed yet, and it is a **latch** rather than a
   * live read of `bookSelection`. An auto-opened book has no tap behind it, so the entry waits for
   * the first tick, which is one; deriving the condition from the selection directly would unwind
   * the entry the moment a manager unticked back to nothing and push a fresh one on the next tick,
   * which is history churn for a state the manager never left.
   */
  const [bookBackArmed, setBookBackArmed] = useState(false)
  const [manualInput, setManualInput] = useState('')
  const [manualError, setManualError] = useState(false)
  const [registerError, setRegisterError] = useState(false)
  /**
   * D344 — the iPhone manager's own *None* (`contactBookAccess()` answered `'refused'`): the one
   * state that draws the Settings line under *Add from contacts*. Set by the mount peek and again
   * after a tap that met a refusal, cleared when a return from Settings finds access on.
   */
  const [refused, setRefused] = useState(false)
  /**
   * D346 (g) — the numbers holding a live grant on this gate (`holdsLiveGrant`), normalized to
   * `E164`, for the book to grey. **Component state only, never persisted** — gateHandoff's rule 5
   * for a roster read, and the book's own rule besides. Empty until the read answers and empty for
   * good if it fails: the book fails open and the server's `skipped-duplicate` is the backstop.
   */
  const [members, setMembers] = useState<ReadonlySet<E164>>(() => new Set())
  /**
   * **`session.request` is held in a ref, never put in an effect's dependency array** (`AGENTS.md`
   * §4.9, `native/AGENTS.md`'s first bullet): the function has a new identity after every API call,
   * so an effect keyed on it re-arms on the response it just produced — measured once at 2,965
   * requests from one screen view.
   */
  const requestRef = useRef(session.request)
  requestRef.current = session.request

  /** Guards the mount-time auto-open against StrictMode's double effect invocation. */
  const autoPrompted = useRef(false)

  /**
   * D242 — read once, at mount, from the platform rather than discovered by rejection. A lazy
   * initializer rather than a `useMemo`: the answer cannot change during a launch, and a value
   * React is free to recompute is a value that could disagree with itself between the mount
   * effect that must *not* prompt and the tap handler that must.
   */
  const [mode] = useState(() => contactPickerMode({ multiple: true }))

  useEffect(() => {
    let current = true
    void contactPickerProperties().then((available) => {
      if (!current) return
      setProperties(available)
      /*
       * **The suppression, and it is the whole of D242's out-of-context posture.** In
       * `'address-book-read'` mode this effect would raise Android's `READ_CONTACTS` dialog from
       * a mount, with no user action behind it — the shape store review flags. The button below
       * still opens the book; it just takes a tap to do it, and the rationale line sits beside
       * that button. Every other mode keeps D97's unprompted open exactly as it was.
       *
       * **D250 narrows that suppression to what it was defending, which was the DIALOG.** Where
       * the grant is already held, `readContactBook` reads `getPermissionState`, finds `GRANTED`
       * and presents nothing — so there is no out-of-context prompt to suppress, and suppressing
       * the open anyway cost the Android container the one behaviour every other surface has.
       * The read is a peek: `contactBookAccess` calls Capacitor's inherited `checkPermissions`,
       * which prompts nothing, and **anything short of a positive `'granted'` keeps D242's tap**.
       */
      if (available === undefined || autoPrompted.current) return
      if (mode !== 'address-book-read') {
        autoPrompted.current = true
        void openContacts(available, true)
        return
      }
      void contactBookAccess().then((access) => {
        // The bridge round trip outlives the mount on a slow device; `current` is what stops a
        // book opening onto a screen the manager has already left.
        if (!current) return
        setRefused(access === 'refused')
        if (access !== 'granted' || autoPrompted.current) return
        autoPrompted.current = true
        void openContacts(available, true)
      })
    })
    return () => {
      current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
   * D346 (g) — the roster read, at mount and in parallel with the book, **only where there is a
   * book to grey**: `'os-picker'` mode hands back an OS picker's selection and renders no list of
   * ours, so the read would buy nothing there. One request per screen view: the effect depends on
   * the gate and the (fixed) mode, never on the request function, which is read through its ref.
   * **Not taken from `gateHandoff`** (plan §6 S2's rejected alternative, D265): People re-reads its
   * roster after every mutation and only its initial load may take a deposit, so an invite-bound
   * deposit Invite failed to take would re-seed People with a pre-invite roster on the way back.
   *
   * Every failure — a non-2xx, a network error, a malformed body — is the empty set: no retry and
   * no error line, because the only consequence of not knowing is greying nothing.
   */
  useEffect(() => {
    if (mode !== 'address-book-read') return
    let current = true
    void (async () => {
      let live: ReadonlySet<E164> = new Set()
      try {
        const result = await getRoster(requestRef.current, gateId)
        if (result.ok) live = liveGrantPhones(result.data.members)
      } catch {
        // Fails open, by decision (D346 (g)).
      }
      if (current) setMembers(live)
    })()
    return () => {
      current = false
    }
  }, [gateId, mode])

  function addEntry(phone: E164, source: InviteSource, name?: string, kind?: PhoneKind): void {
    setEntries((prev) =>
      prev.some((entry) => entry.phone === phone)
        ? prev
        : [
            ...prev,
            {
              phone,
              source,
              role: 'resident',
              ...(name === undefined ? {} : { name }),
              ...(kind === undefined ? {} : { kind }),
            },
          ],
    )
  }

  function removeEntry(phone: E164): void {
    setEntries((prev) => prev.filter((entry) => entry.phone !== phone))
  }

  function setRole(phone: E164, role: MemberRole): void {
    setEntries((prev) => prev.map((entry) => (entry.phone === phone ? { ...entry, role } : entry)))
  }

  /**
   * The one-off OS picker — every browser, and the silent fallback on both platforms when the book
   * is refused. `multiple` is a parameter rather than a constant because Android's fallback is
   * single-select by construction: its permission-free intent picks one row, and asking it for
   * several would promise a sweep it cannot make. iOS's is plural (D340).
   */
  async function runPicker(props: readonly ContactProperty[], multiple: boolean): Promise<void> {
    const result = await pickContacts({ multiple, properties: props })
    if (result.kind === 'unavailable') {
      setProperties(undefined)
      return
    }
    if (result.kind !== 'selected') return
    /*
     * **The one place left that takes a contact's FIRST number** (D346 narrowed the defect to it):
     * the PWA's Chrome multi-picker and the iPhone's Apple-picker fallback under *None* are OS
     * pickers with no list of ours to ask which in, so a contact's second number is still
     * unreachable from here (plan §7; `STATE.md` §5, *Filed with D242*).
     */
    for (const selection of contactPhoneSelections(result.contacts)) {
      const phone = selection.phones[0]
      if (phone !== undefined) addEntry(phone, 'picker', selection.name, selection.kinds?.[0])
    }
  }

  /**
   * The one entry point both the mount effect and the button use, so the two cannot diverge on
   * which surface this platform has.
   *
   * **The `denied` branch is silent by decision.** `readContactBook` reads `getPermissionState`
   * before prompting, so a permanent refusal costs no dialog and is not rediscovered on every
   * open; the manager simply gets the picker Android always had, with no message, no toast and no
   * retry loop. `unavailable` and `failed` take the same exit for the same reason — the honest
   * answer to "the bridge is not what we think it is" is the surface that still works, not a
   * sentence about it. A `cancelled` book (the prompt dismissed, or a book with nothing dialable
   * in it) opens nothing: the manager just declined a list, and a second surface they did not ask
   * for would be the reverse of that answer.
   */
  async function openContacts(props: readonly ContactProperty[], auto = false): Promise<void> {
    setPickerBusy(true)
    try {
      if (mode !== 'address-book-read') {
        await runPicker(props, true)
        return
      }
      const book = await readContactBook()
      if (book.kind === 'read') {
        // **`auto` travels with the phase, not with a ref.** What it decides is when the book's
        // back entry may be pushed (D250), and that is a property of *this* opening: the same
        // screen can auto-open once and be re-entered by a tap afterwards.
        setPhase({ kind: 'book', contacts: book.contacts, auto, limited: book.limited })
        return
      }
      if (book.kind === 'cancelled') return
      // **The fallback's multiplicity is the platform's, never a constant.** Android's intent picks
      // one row; iOS's picker is plural for free, and a manager who declined the Contacts dialog
      // keeps the sweep they had before D340 (D340's device check 3).
      await runPicker(props, osPickerMultiSelects())
      // D344 — a refusal met at the tap (the dialog just declined, or a *None* set since the mount
      // peek) draws the Settings line from here on. Peeked rather than inferred from `denied`, which
      // Android answers too and which the line must never follow there.
      if (book.kind === 'denied') setRefused((await contactBookAccess()) === 'refused')
    } finally {
      setPickerBusy(false)
    }
  }

  /**
   * D344 — the Settings line's link. The manager allows access in Settings and comes back; if the
   * peek now reads a grant (full or limited), the line goes and **the book opens**, the manager's tap
   * on the link being what started the trip. It opens as `auto`, so D250's back entry still waits
   * for a tick, the return itself not being a gesture. Still refused, or Settings would not open,
   * and the screen stays as it was.
   */
  async function allowInSettings(props: readonly ContactProperty[]): Promise<void> {
    const settings = await openContactSettings()
    if (settings.kind !== 'returned') return
    const access = await contactBookAccess()
    setRefused(access === 'refused')
    if (access === 'granted') await openContacts(props, true)
  }

  function addFromBook(selections: readonly ContactBookSelection[]): void {
    for (const selection of selections) addEntry(selection.phone, 'picker', selection.name, selection.kind)
    closeBook()
  }

  /**
   * D343 — a limited grant grows in Settings. The manager edits which contacts are shared, or
   * allows them all, and comes back; the book is then **re-read** through the one read this screen
   * already has, and the phase keeps its `auto` so D250's back rule is untouched. The list stays
   * mounted, which is what keeps its ticks; it answers `true` on a successful re-read so the list
   * clears its search, a query typed before the trip being able to hide the person just shared.
   *
   * **Only a successful re-read replaces the book.** A manager who revoked the grant in Settings
   * reads `denied` here, and the honest answer to that is the list they are looking at until they
   * leave it, not a phase change under their thumb. A manager who allowed every contact reads a
   * full book, and the limited line goes with the `limited` flag.
   *
   * D341 reached this from inside the list through Apple's contact-access button, in a native sheet
   * seeded with the search; on iOS 26+ hardware that button is blank or dead — Apple's defect, like
   * the picker beside it — so Settings is the one surface that works.
   */
  async function shareMoreInSettings(): Promise<boolean> {
    const settings = await openContactSettings()
    if (settings.kind !== 'returned') return false
    const book = await readContactBook()
    if (book.kind !== 'read') return false
    setPhase((current) =>
      current.kind === 'book' ? { ...current, contacts: book.contacts, limited: book.limited } : current,
    )
    return true
  }

  function closeBook(): void {
    setLeaveConfirm(false)
    setBookSelection([])
    setBookBackArmed(false)
    setPhase({ kind: 'collect' })
  }

  /**
   * The one exit from the book, so the app bar's chevron and the phone's back gesture cannot
   * diverge on what leaving means.
   *
   * **With nothing ticked it just leaves.** A dialog over an empty selection is a tax on the
   * commonest exit and protects nothing; the question only exists because the ticks are otherwise
   * discarded in silence.
   */
  function leaveBook(): void {
    if (bookSelection.length === 0) {
      closeBook()
      return
    }
    setLeaveConfirm(true)
  }

  /*
   * D145, and legal on this screen because every push below sits behind a gesture. `open` drops
   * while the confirm stands because the entry has already been consumed by the press that raised
   * it — the hook clears `ownsEntry` before calling back, so this is bookkeeping rather than a
   * second traversal.
   *
   * **D250 — a tap-opened book pushes on entry; an auto-opened one pushes on its first tick.**
   * D242's book was reachable only from a tap, which is what made this entry legal at all: Chrome
   * marks a gesture-less `pushState` skippable, and a skipped entry means the phone's back leaves
   * the whole screen. D250 opens the book at mount where the grant is already held, and that
   * opening has no gesture — so the registration waits for the manager's first tick, which does.
   *
   * 🔸 **The stated cost is one press, and it is bounded by construction.** A back pressed on an
   * auto-opened book with nothing ticked leaves the screen rather than falling back to the collect
   * step. The auto-open fires from the mount effect, so `entries` is empty whenever it happens and
   * there is nothing on that step to lose — and `leaveBook` answers an empty selection by leaving
   * anyway. What differs is only where the manager lands. **Not verified on a device.**
   */
  const bookBackOpen =
    phase.kind === 'book' && !leaveConfirm && (!phase.auto || bookBackArmed || bookSelection.length > 0)
  useDismissOnBack('invite:book', bookBackOpen, leaveBook)

  /* The latch. Set from the render that follows the tick rather than inside the hook's condition,
     so an unticked-back-to-nothing selection does not retract an entry the manager still owns. */
  useEffect(() => {
    if (bookSelection.length > 0) setBookBackArmed(true)
  }, [bookSelection])

  function addManual(): void {
    try {
      const phone = normalizePhone(manualInput, 'IL', 'phone')
      addEntry(phone, 'manual')
      setManualInput('')
      setManualError(false)
    } catch {
      setManualError(true)
    }
  }

  async function register(): Promise<void> {
    if (entries.length === 0) return
    setPhase({ kind: 'registering' })
    setRegisterError(false)
    try {
      const response = await bulkInviteMembers(
        session.request,
        gateId,
        // No `transportRestriction`: every invite is the default, both transports (D46 — the
        // absence IS the default). People's member sheet restricts a grant after the fact (D269).
        entries.map(({ phone, source, role }) => ({ phone, source, role })),
        locale,
      )
      if (!response.ok) {
        setRegisterError(true)
        setPhase({ kind: 'collect' })
        return
      }
      setPhase({ kind: 'results', result: response.data })
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setRegisterError(true)
      setPhase({ kind: 'collect' })
    }
  }

  function done(): void {
    navigate.resetTo({ id: 'people', gateId })
  }

  if (phase.kind === 'book') {
    return (
      <>
        {/*
          D346 (b) — Add moves here from the sheet's own footer, as the app bar's trailing action
          (D247's slot, gaining its second consumer). `bookSelection` is the list the sheet already
          reports through `onSelectionChange`, so the list the leave-confirm asks about and the
          list *Add* adds are still the one list the sheet resolves.
        */}
        <AppBar
          title={t('addTo', { name: gateName })}
          onBack={leaveBook}
          action={{
            label: t('bulkBookAddCount', { count: bookSelection.length }),
            disabled: bookSelection.length === 0,
            onClick: () => addFromBook(bookSelection),
          }}
        />
        <ContactBookSheet
          contacts={phase.contacts}
          limited={phase.limited}
          onShareMore={shareMoreInSettings}
          remaining={MAX_BULK_ROWS - entries.length}
          onSelectionChange={setBookSelection}
          members={members}
          t={t}
        />
        {/*
          **The two answers back has, stated rather than assumed.** Leaving discards the ticks, so
          the question is put with both outcomes on buttons — which is also what retired the list's
          own Cancel, that having been one of these two answers offered in a place where the other
          one was not.

          `ConfirmPopover` focuses **cancel**, and here cancel is the discarding half. That is the
          component's rule rather than an oversight on this call: neither answer is destructive to
          anything the server holds, and a stray Enter that adds contacts nobody meant to add is
          the worse of the two accidents.
        */}
        {leaveConfirm ? (
          <FrameOverlay>
            <ConfirmPopover
              title={t('bulkBookLeaveTitle')}
              body={t('bulkBookLeaveBody')}
              cancelLabel={t('bulkBookCancel')}
              confirmLabel={t('bulkBookAdd')}
              // The discarding half here is *cancel*, so the danger colours would read backwards
              // on Add — the one caller of this dialog that is not confirming a destruction.
              destructive={false}
              onCancel={closeBook}
              onConfirm={() => addFromBook(bookSelection)}
            />
          </FrameOverlay>
        ) : null}
      </>
    )
  }

  if (phase.kind === 'results') {
    const { result } = phase
    const rows: readonly OutcomeRowView[] = result.rows.map((row) => outcomeRowView(row, entries, t))
    return (
      <>
        <AppBar title={t('bulkResultsTitle')} onBack={done} />
        <OutcomeReport rows={rows} />
        {result.summary.registered > 0 || result.summary.added > 0 ? (
          <>
            <div className="section-title">{t('shareTitle')}</div>
            <ShareSheet
              heading={t('shareTitle')}
              message={result.share.text}
              onSelect={(channel) => {
                if (channel === 'sms')
                  window.open(`sms:?body=${encodeURIComponent(result.share.text)}`, '_blank')
                else if (channel === 'whatsapp')
                  window.open(`https://wa.me/?text=${encodeURIComponent(result.share.text)}`, '_blank')
                else if (navigator.share !== undefined) void navigator.share({ text: result.share.text })
              }}
            />
          </>
        ) : null}
        <div className="form-row">
          <button className="btn-secondary" type="button" onClick={done}>
            {t('done')}
          </button>
        </div>
      </>
    )
  }

  const busy = phase.kind === 'registering'
  const anyManager = entries.some((entry) => entry.role === 'manager')

  return (
    <>
      <AppBar title={t('addTo', { name: gateName })} onBack={() => navigate.back()} />
      {/*
        **Two ways in, one at a time.** The contacts button and the phone field were stacked, and a
        manager only ever uses one of them — so between them they spent the top of a screen whose
        subject is the list underneath. The pill is `PreferencesScreen`'s appearance control's own
        `.scheme-segmented`, reused rather than re-cut: a second segmented treatment is a second
        thing to keep in step with the palette.

        **It is absent, not disabled, where there is no contacts surface** (a desktop browser, iOS
        Safari — `contactPickerProperties()` answers `undefined`). A selectable row that refuses
        every tap is strictly worse than no row, which is D200's finding one namespace over; with
        no pill the manual fields render unconditionally, which is exactly what that platform had
        before this.

        `aria-pressed`, not `aria-checked`: a toggle group, not a radiogroup — the same reading the
        appearance pill takes.
      */}
      {properties !== undefined ? (
        <div className="scheme-row">
          <div className="scheme-segmented" role="group" aria-label={t('bulkTabsLabel')}>
            <button
              className={`scheme-segment${tab === 'contacts' ? ' selected' : ''}`}
              type="button"
              aria-pressed={tab === 'contacts'}
              onClick={() => setTab('contacts')}
            >
              <span>{t('bulkTabContacts')}</span>
            </button>
            <button
              className={`scheme-segment${tab === 'manual' ? ' selected' : ''}`}
              type="button"
              aria-pressed={tab === 'manual'}
              onClick={() => setTab('manual')}
            >
              <span>{t('bulkTabManual')}</span>
            </button>
          </div>
        </div>
      ) : null}
      {properties !== undefined && tab === 'contacts' ? (
        <div className="form-row">
          <SecondaryButton disabled={pickerBusy || busy} onClick={() => void openContacts(properties)}>
            {pickerBusy
              ? t(mode === 'address-book-read' ? 'bulkBookOpening' : 'bulkContactsOpening')
              : t('bulkAddFromContacts')}
          </SecondaryButton>
          {/*
            **D242's in-context rationale, and it is placed for a reader who is not the manager.**
            Android's `READ_CONTACTS` prompt is raised by this button, so the sentence explaining
            what the read costs has to be on screen *before* the tap — that is what makes it
            in-context, and it is the line a store reviewer reads in a screenshot of this step.
            It is absent in `'os-picker'` mode because there is nothing to explain there: the
            out-of-process picker hands back only the selection and asks for no permission, which
            is what `contacts.json`'s own rationale (still singular, still true) says on the
            install step.
          */}
          {mode === 'address-book-read' ? <p className="hint">{t('bulkBookRationale')}</p> : null}
          {/*
            **D344 — an offer, drawn only for the iPhone manager's own *None*.** It sits beside the
            button rather than after the picker so it is read before the tap; the tap still opens
            Apple's picker exactly as it did, so the line gates nothing and is not an error surface.
          */}
          {mode === 'address-book-read' && refused ? (
            <p className="hint">
              {t('bulkBookDeniedHint')}{' '}
              <LinkButton disabled={pickerBusy || busy} onClick={() => void allowInSettings(properties)}>
                {t('bulkBookAllowInSettings')}
              </LinkButton>
            </p>
          ) : null}
        </div>
      ) : null}
      {properties === undefined || tab === 'manual' ? (
        <>
          <div className="form-row">
            <label htmlFor="invitePhone">{t('phoneLabel')}</label>
            <input
              id="invitePhone"
              className="input"
              type="tel"
              dir="ltr"
              placeholder={t('phonePlaceholder')}
              value={manualInput}
              disabled={busy}
              onChange={(e) => {
                setManualInput(e.target.value)
                setManualError(false)
              }}
              aria-invalid={manualError}
            />
            {manualError ? <p className="hint error">{t('phoneInvalid')}</p> : null}
          </div>
          <div className="form-row">
            <button
              className="btn-secondary"
              type="button"
              disabled={busy || manualInput.trim().length === 0}
              onClick={addManual}
            >
              {t('bulkManualAdd')}
            </button>
          </div>
        </>
      ) : null}

      <div className="section-title">{t('bulkCount', { count: entries.length })}</div>
      {entries.length === 0 ? (
        <div className="row">
          <div className="l">{t('bulkEmpty')}</div>
        </div>
      ) : (
        entries.map((entry) => (
          <div className="row invite-entry-row" key={entry.phone}>
            {/*
              **D242 — the name leads and the number stays.** Twenty rows of bare `+9725…` are
              not checkable against who the manager meant to invite, so the confirm step stops
              working at exactly the list size this whole change exists to make easy. The number
              stays underneath because it is what the grant is actually made against, and because
              two contacts can carry one name. `unicode-bidi: plaintext` on the name is not
              optional in an RTL shell: an address book holds Latin names beside Hebrew ones, and
              Hebrew-only seed data hides that entire class of bug.

              **D346 — the same avatar the book wears.** A manually typed entry carries no name
              (`addManual` never sets one), so `ContactAvatar` draws the `user` glyph for it by the
              same "no letter in the name" rule that draws it for a contact filed under a number
              alone — no `source` branch needed here.
            */}
            <div className="l">
              {/*
                **Remove is a bare red trash glyph at the row's leading edge**, the opposite end
                from the role chip — a destructive control kept apart from the one that edits the
                grant, so a thumb reaching for the chip never lands on it. No
                container: the glyph and its colour are the whole affordance, and `bulkRemove`
                stays its accessible name, the icon being `aria-hidden`.
              */}
              <button
                className="invite-entry-remove"
                type="button"
                disabled={busy}
                aria-label={t('bulkRemove')}
                onClick={() => removeEntry(entry.phone)}
              >
                <Icon name="trash-2" size={18} />
              </button>
              <ContactAvatar
                {...(entry.name === undefined ? {} : { name: entry.name })}
                phone={entry.phone}
              />
              <div className="invite-entry-text">
                {entry.name === undefined ? (
                  <div dir="ltr">{entry.phone}</div>
                ) : (
                  <>
                    <div style={{ unicodeBidi: 'plaintext' }}>{entry.name}</div>
                    <div className="gsub">
                      <span dir="ltr">{entry.phone}</span>
                      {/* D346 (f) — the chosen number's type, where the book supplied one. */}
                      {entry.kind === undefined ? null : (
                        <span className="book-row-meta">{t(PHONE_KIND_KEYS[entry.kind])}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="r" style={{ display: 'flex', gap: 8 }}>
              <button
                className={`steel-chip${entry.role === 'manager' ? ' on' : ''}`}
                type="button"
                disabled={busy}
                aria-pressed={entry.role === 'manager'}
                onClick={() => setRole(entry.phone, entry.role === 'manager' ? 'resident' : 'manager')}
              >
                {entry.role === 'manager' ? t('roleManager') : t('roleResident')}
              </button>
            </div>
          </div>
        ))
      )}
      {anyManager ? <p className="hint">{t('roleManagerHint')}</p> : null}
      {/*
        **The BLE-only warning is gone, and the premise it rested on is what went.** It told a
        manager that an iPhone holding such a grant could not open the gate from the app at all —
        true only while the sole iOS radio was Web Bluetooth, which Safari does not have. The
        native container ships a Swift BLE leaf (`AGENTS.md` §4.9), so an iPhone now holds the same
        local path an Android handset does and the sentence had become a false refusal notice on a
        grant that works. Restoring it needs the transport asymmetry back, not a copy edit.
      */}
      {registerError ? <p className="hint error">{t('bulkRegisterFailed')}</p> : null}
      {/*
        **Pinned.** This was the last child in document flow, which put it below however many people
        had just been added — off screen at exactly the list size the bulk path exists for. The two
        hints and the failure line stay above it, in the scroll: a sentence qualifying a button is
        only a qualification while it is read first.
      */}
      <div className="screen-footer">
        <div className="form-row">
          <button
            className="btn-primary"
            type="button"
            disabled={busy || entries.length === 0}
            onClick={() => void register()}
          >
            {busy ? t('bulkRegistering') : t('bulkContinue', { count: entries.length })}
          </button>
        </div>
      </div>
    </>
  )
}

function outcomeRowView(
  row: BulkInviteResult['rows'][number],
  entries: readonly Entry[],
  t: (key: Key<'invite'>, vars?: Readonly<Record<string, string | number>>) => string,
): OutcomeRowView {
  const entry = entries[row.index]
  const phone = row.phone ?? entry?.phone ?? ''
  const outcomeLabel =
    row.outcome === 'registered'
      ? t('bulkOutcomeRegistered')
      : row.outcome === 'added'
        ? t('bulkOutcomeAdded')
        : row.outcome === 'skipped-duplicate'
          ? t('bulkOutcomeSkippedDuplicate')
          : t('bulkOutcomeInvalid')
  const tone: OutcomeTone =
    row.outcome === 'registered' || row.outcome === 'added'
      ? 'positive'
      : row.outcome === 'invalid'
        ? 'negative'
        : 'neutral'
  /*
   * **D242 — the report prefers the name for the same reason the collect list does.** A results
   * screen is where a manager confirms that the twenty people they picked are the twenty who were
   * registered, and twenty numbers cannot be read against twenty intentions. The phone drops to
   * `detail` rather than out: an `invalid` row is only actionable if it says *which number* was
   * refused, and the name a phone was filed under does not identify it to the server.
   */
  const name = entry?.name
  return {
    key: `${row.index}`,
    label: name ?? phone,
    ...(name === undefined ? {} : { detail: phone }),
    outcomeLabel,
    tone,
  }
}

/**
 * D346 (g) — the roster's live grants as the book's `E164` set. **Each phone goes through
 * `normalizePhone`**, the book's own normalizer: comparing the roster's raw strings to `E164`
 * would leave every row ungreyed with nothing red. A phone that fails to normalize is skipped.
 */
function liveGrantPhones(rows: readonly RosterRow[]): ReadonlySet<E164> {
  const phones = new Set<E164>()
  for (const row of rows) {
    if (!holdsLiveGrant(row) || row.phone === undefined) continue
    try {
      phones.add(normalizePhone(row.phone, 'IL', 'phone'))
    } catch {
      // Not a number this flow can dial, so no book row could match it.
    }
  }
  return phones
}
